import axios from "axios";
import { getToken, removeToken } from "@/utils/auth";
import router from "@/router";
import { useUserStoreActions } from "@/hooks/store/useUserStore";
import useToast from "@/hooks/useToast";
import { blobErrorMessage } from "@/utils/blobErrorMessage";

const { openToast } = useToast();
const isInvalidResponseType = (response) => {
  const INVALID_TYPES = [
    "text/html; charset=utf-8",
    "text/html; charset=UTF-8",
    "text/html; charset=ISO-8859-1",
    "text/html;",
    "text/html",
    "text/plain",
  ];
  return response && INVALID_TYPES.includes(response.headers["content-type"]);
};

// When the session is over, the back end may answer an API call with a 302 to its login page
// instead of a 401. The browser follows it on its own, so all we get is that page's HTML.
const isLoginPageRedirect = (response) =>
  /\/users\/login(\?|$)/.test(response?.request?.responseURL || "");

const isLogoutRequest = (config) => /\/api\/V2\/logout$/.test(config?.url || "");

// Several requests usually fail together when the session ends; log out and tell the user
// once, not once per request.
let isEndingSession = false;
const endSession = () => {
  if (isEndingSession) return;
  isEndingSession = true;
  const { logout } = useUserStoreActions();
  // Sent with the token that has just expired, so it usually fails; nothing to do about that.
  Promise.resolve(logout()).catch(() => {});
  removeToken();
  localStorage.clear();
  openToast("Unauthorized. Try again!");
  Promise.resolve(router.push("/")).finally(() => {
    isEndingSession = false;
  });
};

const request = axios.create({
  timeout: 70000,
});

// No default Content-Type: axios 1 sets application/json for plain objects by itself, and a
// forced JSON type makes it serialize FormData to JSON, dropping uploaded files.
request.defaults.headers.common.Accept = "application/json";

const hasFiles = (form) => [...form.values()].some((value) => value instanceof Blob);

request.interceptors.request.use(
  (config) => {
    // Forms without files go as JSON, as they always have: PHP does not parse a multipart body
    // of a PUT or PATCH, so the back end would see no fields. Forms with files stay multipart.
    const asJson = config.data instanceof FormData && !hasFiles(config.data);
    const TOKEN = getToken();
    // By the time interceptors run axios has already merged headers.common into config.headers,
    // so the token has to be set on config.headers itself.
    config.headers.set("Authorization", TOKEN ? `Bearer ${TOKEN}` : "");
    return asJson ? { ...config, data: axios.formToJSON(config.data) } : config;
  },
  (error) =>
    // Do something with request error
    Promise.reject(error)
);

request.interceptors.response.use(
  (response) => {
    if (isLoginPageRedirect(response)) {
      endSession();
      const error = new Error("Session expired");
      error.response = response;
      throw error;
    }
    if (isInvalidResponseType(response)) {
      const error = new Error("Invalid response type");
      error.response = response;
      openToast(error.message);
      throw error;
    }
    return response;
  },
  async (error) => {
    // A request made for a file (responseType "blob") gets its error body as a Blob too;
    // read the server's message out of it so the checks and the toast below can see it.
    const blobMessage = await blobErrorMessage(error?.response?.data);
    if (blobMessage) {
      Object.assign(error.response, { data: { message: blobMessage } });
    }
    // Only the status is checked: over HTTP/2 there is no reason phrase, so statusText is "".
    if (isLogoutRequest(error.config)) {
      // The logout call made by endSession() itself: ending the session again would loop.
    } else if (
      error.response.status === 401 ||
      (error.response.status === 500 && error.response.data.message === "Expired token")
    ) {
      endSession();
    } else if (error.response.status >= 400) {
      const message = error?.response?.data?.message || error?.message || "Error";
      openToast(message);
    }
    return Promise.reject(error);
  }
);
/**
 * @param {string} url
 * @param {string} method
 * @param {object} data
 *
 */
export default request;
